/**
 * contentDetector.js — Detects text blocks and images in PDF pages.
 *
 * Uses PDF.js getTextContent() for text and getOperatorList() for images.
 * Groups raw text items into logical blocks for editing.
 */

import { pdfjsLib } from './pdfWorker';

/**
 * Phase 1: Detect text blocks only. Fast — uses getTextContent() which is
 * pure metadata parsing, no canvas work, no operator list.
 * Returns immediately usable text blocks.
 */
export async function detectTextContent(page, pageNum, geometry) {
  const viewport = page.getViewport({ scale: 1, rotation: 0 });
  const textBlocks = await detectTextBlocks(page, pageNum, geometry?.baseWidth || viewport.width, geometry?.baseHeight || viewport.height);
  return { textBlocks };
}

/**
 * Phase 2: Detect images. Slow — uses getOperatorList() + canvas extraction.
 * Must be called on a SEPARATE pdfDoc instance to avoid render conflicts.
 * Returns image blocks only.
 */
export async function detectImageContent(page, pageNum, geometry) {
  const viewport = page.getViewport({ scale: 1, rotation: 0 });
  const images = await detectImages(page, pageNum, geometry?.baseWidth || viewport.width, geometry?.baseHeight || viewport.height);
  return { images };
}

/**
 * Detect all editable content on a single PDF page.
 *
 * @param {PDFPageProxy} page — PDF.js page object
 * @param {number} pageNum — 1-indexed page number
 * @returns {Promise<{ textBlocks: TextBlock[], images: ImageBlock[] }>}
 *
 * TextBlock shape:
 *   { id, pageNum, x, y, width, height, content, lines: [{ items: [TextItem] }],
 *     fontFamily, fontSize, color, alignment }
 *
 * ImageBlock shape:
 *   { id, pageNum, x, y, width, height, dataUrl }
 */
export async function detectPageContent(page, pageNum) {
  const viewport = page.getViewport({ scale: 1, rotation: 0 });
  const pageHeight = viewport.height;
  const pageWidth = viewport.width;

  const [textBlocks, images] = await Promise.all([
    detectTextBlocks(page, pageNum, pageWidth, pageHeight),
    detectImages(page, pageNum, pageWidth, pageHeight),
  ]);

  return { textBlocks, images };
}

/**
 * Detect content on all pages of a PDF document.
 *
 * @param {PDFDocumentProxy} pdfDoc — PDF.js document
 * @param {(progress: number) => void} onProgress
 * @returns {Promise<Map<number, { textBlocks: TextBlock[], images: ImageBlock[] }>>}
 */
export async function detectAllPages(pdfDoc, onProgress = () => {}) {
  const results = new Map();
  const numPages = pdfDoc.numPages;

  for (let i = 1; i <= numPages; i++) {
    const page = await pdfDoc.getPage(i);
    const content = await detectPageContent(page, i);
    results.set(i, content);
    onProgress(i / numPages);
  }

  return results;
}

/**
 * Detect and group text content into editable blocks.
 */
async function detectTextBlocks(page, pageNum, pageWidth, pageHeight) {
  const textContent = await page.getTextContent({ includeMarkedContent: false });
  const items = textContent.items;
  const styles = textContent.styles || null; // per-fontName {fontFamily, ascent, descent, vertical}; not always present (test mocks)

  if (!items || items.length === 0) return [];

  // TextContent uses internal font IDs and omits color. Resolve the loaded
  // font and the corresponding painted text run before promoting it to an
  // editable box, so bold/italic/colored source text keeps its styling.
  let paintedText = '', paintedColors = [], colorCursor = 0;
  if (page.getOperatorList) {
    const ops = await page.getOperatorList();
    const stack = []; let color = '#000000';
    for (let i = 0; i < ops.fnArray.length; i++) {
      const op = ops.fnArray[i], args = ops.argsArray[i];
      if (op === pdfjsLib.OPS.save) stack.push(color);
      else if (op === pdfjsLib.OPS.restore) color = stack.pop() || '#000000';
      else if (op === pdfjsLib.OPS.setFillRGBColor) {
        color = typeof args[0] === 'string' ? args[0] : '#' + Array.from(args).map((n) => Math.round(n).toString(16).padStart(2, '0')).join('');
      } else if (op === pdfjsLib.OPS.showText || op === pdfjsLib.OPS.showSpacedText) {
        const text = (args[0] || []).map((glyph) => glyph?.unicode || '').join('').replace(/\s/g, '');
        paintedText += text; paintedColors.push(...Array(text.length).fill(color));
      }
    }
  }


  // Fallback metrics used when a real styles entry isn't available for an
  // item's fontName (e.g. the legacy no-styles test mock). 0.8/-0.2 reduces
  // to the old fontSize*1.2 line-height guess so pre-existing rotation
  // fixtures (which use a styles-less mock) keep asserting the same values.
  const FALLBACK_ASCENT = 0.8;
  const FALLBACK_DESCENT = -0.2;

  // Transform each text item to page coordinates (top-left origin)
  // PDF.js textContent items have a `transform` array: [scaleX, skewY, skewX, scaleY, translateX, translateY]
  // translateX/translateY is in PDF coordinates (bottom-left origin)
  const positioned = items
    .filter((item) => item.str && item.str.trim())
    .map((item) => {
      const tx = item.transform;
      // Rotation: atan2 of the transform's skew terms. Anything beyond ~0.5°
      // is treated as rotated (tiny float skew on straight text is common).
      const angle = Math.atan2(tx[1], tx[0]);
      const rotated = Math.abs(angle) > 0.0087; // ~0.5° in radians
      // hypot survives rotation (a pure-rotation matrix zeroes tx[3]/tx[0],
      // which used to silently fall back to 12pt for rotated text).
      const fontSize = Math.hypot(tx[2], tx[3]) || Math.hypot(tx[0], tx[1]) || 12;
      const x = tx[4];
      const baseline = tx[5]; // raw PDF baseline, bottom-left origin

      let loadedFont;
      try { loadedFont = page.commonObjs?.get(item.fontName); } catch { /* unavailable in incomplete source fonts */ }
      const sourceFont = loadedFont?.name || item.fontName || 'Helvetica';
      const match = paintedText.indexOf(item.str.replace(/\s/g, ''), colorCursor);
      const sourceColor = match >= 0 ? paintedColors[match] : '#000000';
      if (match >= 0) colorCursor = match + item.str.replace(/\s/g, '').length;
      const fontStyle = (styles && item.fontName && styles[item.fontName]) || null;
      const ascent = fontStyle && typeof fontStyle.ascent === 'number' ? fontStyle.ascent : FALLBACK_ASCENT;
      const descent = fontStyle && typeof fontStyle.descent === 'number' ? fontStyle.descent : FALLBACK_DESCENT;

      // Top of glyphs (ascent above baseline), converted to top-left page origin.
      const y = pageHeight - baseline - ascent * fontSize;
      const width = item.width || (item.str.length * fontSize * 0.6); // estimate if no width
      const height = (ascent + Math.abs(descent)) * fontSize;

      // Cover extents — used ONLY to build originalBounds (see below), never
      // for item y/height (grouping/display depend on the tight ascent-based
      // values above). A reported ascent/descent can be tighter than a full
      // em (e.g. 0.75/-0.15), which leaves ascenders/cap-height/antialiasing
      // peeking above a mask or save-time whiteout sized to it. Flooring to
      // >=1.0em above baseline and >=0.25em below guarantees full ink coverage.
      const coverTop = pageHeight - baseline - Math.max(ascent, 1.0) * fontSize;
      const coverBottom = pageHeight - baseline + Math.max(Math.abs(descent), 0.25) * fontSize;

      return {
        str: item.str,
        x,
        y,
        width,
        height,
        fontSize,
        baseline,
        ascent,
        descent,
        coverTop,
        coverBottom,
        fontFamily: sourceFont,
        bold: loadedFont?.bold || /Bold|Black|Heavy/i.test(sourceFont),
        italic: loadedFont?.italic || /Italic|Oblique/i.test(sourceFont),
        color: sourceColor || '#000000',
        rotated,
      };
    });

  if (positioned.length === 0) return [];

  // --- Group into lines (items sharing a baseline) ---
  // Baseline proximity, not top-left Y, is what actually identifies "same
  // line": a 12pt run and an 18pt run set on one line share a baseline but
  // have different tops/heights, so Y-tolerance chaining used to split them.
  const lines = [];
  const sorted = [...positioned].sort((a, b) => b.baseline - a.baseline || a.x - b.x);

  let currentLine = [sorted[0]];
  for (let i = 1; i < sorted.length; i++) {
    const item = sorted[i];
    const lastItem = currentLine[currentLine.length - 1];
    const tolerance = 0.3 * Math.min(item.fontSize, lastItem.fontSize);
    if (Math.abs(item.baseline - lastItem.baseline) <= tolerance) {
      currentLine.push(item);
    } else {
      lines.push(currentLine);
      currentLine = [item];
    }
  }
  lines.push(currentLine);

  // Sort items within each line by X
  for (const line of lines) {
    line.sort((a, b) => a.x - b.x);
  }

  // Precompute per-line aggregate info used by both block grouping and output.
  const lineInfo = lines.map((line) => {
    const baseline = mode(line.map((it) => it.baseline));
    const avgFontSize = line.reduce((s, it) => s + it.fontSize, 0) / line.length;
    return { items: line, baseline, avgFontSize };
  });

  // --- Group lines into blocks (consecutive lines with similar X and consistent baseline spacing) ---
  // Conservative tolerances to avoid merging table cells or unrelated columns.
  const BLOCK_X_TOLERANCE = 30; // lines starting within 30px of each other belong to same block

  const blocks = [];
  let currentBlock = [lineInfo[0]];

  for (let i = 1; i < lineInfo.length; i++) {
    const prev = currentBlock[currentBlock.length - 1];
    const curr = lineInfo[i];
    const prevMinX = Math.min(...prev.items.map((it) => it.x));
    const currMinX = Math.min(...curr.items.map((it) => it.x));
    const xAligned = Math.abs(currMinX - prevMinX) < BLOCK_X_TOLERANCE;

    // Don't group lines with very different font sizes (e.g. heading + body text)
    const fontRatio = Math.max(prev.avgFontSize, curr.avgFontSize) / Math.min(prev.avgFontSize, curr.avgFontSize);
    const fontSizeSimilar = fontRatio < 1.4; // within 40% of each other

    // Baselines run top-to-bottom (descending value in PDF space), so a
    // normal next-line baseline delta is positive. Tolerance scales with the
    // smaller of the two line font sizes so tight leading (< 1.2em) no
    // longer forces a split (the old yGap>=0 heuristic did exactly that).
    const baselineDelta = prev.baseline - curr.baseline;
    const maxDelta = 1.6 * Math.min(prev.avgFontSize, curr.avgFontSize);
    const baselineOk = baselineDelta > 0 && baselineDelta <= maxDelta;

    if (xAligned && fontSizeSimilar && baselineOk) {
      currentBlock.push(curr);
    } else {
      blocks.push(currentBlock);
      currentBlock = [curr];
    }
  }
  blocks.push(currentBlock);

  // --- Convert blocks to TextBlock objects ---
  let blockId = 0;
  const textBlocksOut = blocks.map((blockLineInfos) => {
    const blockLines = blockLineInfos.map((li) => li.items);
    const allItems = blockLines.flat();
    const x = Math.min(...allItems.map((it) => it.x));
    const y = Math.min(...allItems.map((it) => it.y));
    const maxX = Math.max(...allItems.map((it) => it.x + it.width));
    const maxY = Math.max(...allItems.map((it) => it.y + it.height));
    // Cover union: the tight per-item COVER extents (>=1em above baseline,
    // >=0.25em below), unioned across the block. x/width are unchanged —
    // only vertical extent differs from the tight y/maxY box above.
    const coverY = Math.min(...allItems.map((it) => it.coverTop));
    const coverMaxY = Math.max(...allItems.map((it) => it.coverBottom));

    // Build content string: join items in each line with spaces, lines with newlines
    const content = blockLines
      .map((line) => line.map((it) => it.str).join(' '))
      .join('\n');

    // Per-line RUNS: split a line wherever fontName changes or the font size
    // jumps by more than 0.5pt, so mixed-size same-baseline text (e.g. an
    // inline bold emphasis run) keeps its true sizes instead of being
    // collapsed to the line's mode() font size.
    const lineSegments = blockLines.map((line) => {
      const runs = [];
      for (const it of line) {
        const last = runs[runs.length - 1];
        const sameRun = last && last.fontName === it.fontFamily &&
          Math.abs(last.fontSize - it.fontSize) <= 0.5 && last.color === it.color;
        if (sameRun) {
          last.text += ' ' + it.str;
        } else {
          runs.push({
            text: it.str,
            fontSize: it.fontSize,
            fontFamily: sanitizeFontFamily(it.fontFamily),
            fontName: it.fontFamily, // internal only, stripped below
            bold: it.bold, italic: it.italic, color: it.color,
          });
        }
      }
      const text = line.map((it) => it.str).join(' ');
      const lineFontSize = mode(line.map((it) => it.fontSize)) || 12;
      const lineFontFamily = sanitizeFontFamily(mode(line.map((it) => it.fontFamily)) || 'Helvetica');
      return {
        text,
        fontSize: lineFontSize,
        fontFamily: lineFontFamily,
        runs: runs.map(({ fontName: _fontName, ...run }) => run),
      };
    });

    // Use the most common font/size in the block (for block-level defaults)
    const fontSizes = allItems.map((it) => it.fontSize);
    const fontSize = mode(fontSizes) || 12;
    const fontFamilies = allItems.map((it) => it.fontFamily);
    const fontFamily = mode(fontFamilies) || 'Helvetica';

    // Real leading, not a hardcoded 1.3 guess. For multi-line blocks, derive
    // it from the median consecutive baseline-to-baseline delta (per-line
    // baselines are already computed in lineInfo above) over the block's own
    // font size — tighter-leaded documents (Word single-spacing, dense
    // reports) then render at their true leading instead of visibly
    // inflating when a block becomes interactive. Single-line blocks have no
    // baseline delta to measure, so they use a sane 1.2 default. Clamped to
    // [1.0, 2.0] to guard against outlier/rotated-line noise, rounded to 2dp.
    const lineHeight = blockLineInfos.length > 1
      ? (() => {
        const deltas = [];
        for (let i = 1; i < blockLineInfos.length; i++) {
          deltas.push(blockLineInfos[i - 1].baseline - blockLineInfos[i].baseline);
        }
        deltas.sort((a, b) => a - b);
        const mid = Math.floor(deltas.length / 2);
        const medianDelta = deltas.length % 2 !== 0
          ? deltas[mid]
          : (deltas[mid - 1] + deltas[mid]) / 2;
        const raw = medianDelta / fontSize;
        const clamped = Math.min(2.0, Math.max(1.0, raw));
        return Math.round(clamped * 100) / 100;
      })()
      : 1.2;

    // Detect alignment from X positions relative to block width
    const blockWidth = maxX - x;
    const lineStartXs = blockLines.map((line) => Math.min(...line.map((it) => it.x)) - x);
    const alignment = detectAlignment(lineStartXs, blockWidth);

    const PADDING = 4; // small padding around detected blocks

    return {
      id: `detected-text-${pageNum}-${blockId++}`,
      pageNum,
      x: Math.max(0, x - PADDING),
      y: Math.max(0, y - PADDING),
      width: Math.min(pageWidth - x, blockWidth + PADDING * 2),
      height: Math.min(pageHeight - y + PADDING, (maxY - y) + PADDING * 2),
      content,
      fontFamily: sanitizeFontFamily(fontFamily),
      fontSize,
      lineHeight,
      color: allItems[0]?.color || '#000000',
      bold: allItems[0]?.bold || false, italic: allItems[0]?.italic || false,
      alignment,
      rotated: blockLines.some((line) => line.some((item) => item.rotated)),
      lineSegments, // per-line RUNS (true per-run size/family) for accurate rendering
      // originalBounds is a COVERAGE box (mask + save whiteout), not a
      // typographic box — it must fully cover rendered ink incl. ascenders
      // and antialiasing, hence >=1em above baseline (and >=0.25em below).
      // x/width match the tight text extents; only the vertical extent is
      // widened, via the per-item cover union computed above.
      originalBounds: { x, y: coverY, width: maxX - x, height: coverMaxY - coverY },
      // Remove only the source text runs, leaving neighboring columns and
      // background artwork intact. The original baseline selects the entire
      // run, including punctuation and spaces; the narrow box also supports
      // older metadata without reaching into densely spaced adjacent lines.
      sourceTextBounds: allItems.map((item) => ({ x: item.x, y: item.y + item.height * .45, width: item.width, height: item.height * .1, baseline: item.baseline })),
      // Tight (ascent/descent-based, un-padded) top/bottom — kept only for
      // the cover-bleed clamp pass below; stripped before this function
      // returns, never part of the public TextBlock shape.
      _tightTop: y,
      _tightBottom: maxY,
    };
  }).filter((block) => {
    // Skip tiny/empty blocks (likely artifacts) — relative to the block's own
    // font size so single glyphs (bullets, "1.", checkmarks) survive.
    const text = block.content.trim();
    return text.length > 0 && block.width > 0.3 * block.fontSize && block.height > 0.5 * block.fontSize;
  });

  clampAdjacentCoverBleed(textBlocksOut);

  for (const block of textBlocksOut) {
    delete block._tightTop;
    delete block._tightBottom;
  }

  return textBlocksOut;
}

// Ink-safety margins for the adjacency clamp below, expressed as a fraction
// of the OWNING block's own fontSize. Measured (see contentDetector.test.js
// "adjacent-block cover-bleed clamp" + the Phase 1.1 measurement harness in
// docs/superpowers/plans's report): pdf.js's reported textContent.styles
// ascent/descent metrics understate a font's REAL rendered ink extent —
// Helvetica measured top-of-ink at 0.924em above baseline (accented capital)
// vs a reported ascent of 0.718em (delta 0.206em); Times measured 0.903em vs
// reported 0.683em (delta 0.220em). Real descent tracks the reported metric
// closely (delta <=0.01em both fonts) — antialiasing/hinting rarely pushes a
// descender meaningfully past the font's own descent value.
//
// The unclamped per-item floor (coverTop/coverBottom above, >=1.0em /
// >=0.25em) already absorbs this: 1.0 - 0.718 = 0.282em / 1.0 - 0.683 =
// 0.317em of headroom above the measured deltas. The clamp below, prior to
// this margin, had ZERO headroom — it cut a block's cover edge exactly to
// the midpoint of the TIGHT (reported-ascent-based) gap, which can land
// inside a neighbor's real ink when that neighbor's true glyph extent
// exceeds its reported metric (verified: an accented capital's top row of
// antialiased ink survived a whiteout clamped this way, both fonts, at
// zoom 1/1.5/2). SAFETY_ABOVE=0.25 and SAFETY_BELOW=0.05 give >=14% (Times; Helvetica ~21%) and
// >=400% margin over the respective measured deltas — bleeding into the
// inter-block WHITESPACE when the safety pad pushes past the raw midpoint is
// harmless (it's blank page); exposing a neighbor's ink is not.
const CLAMP_SAFETY_ABOVE = 0.25;
const CLAMP_SAFETY_BELOW = 0.05;

/**
 * Post-assembly pass: clamp cover-rect bleed between adjacent blocks.
 *
 * originalBounds (the "cover" rect — see its doc comment above) floors to
 * >=1em above baseline and >=0.25em below, per-item, then unions across a
 * block. Two blocks split apart by the font-ratio (>=1.4x) or x-misalignment
 * heuristics above can still sit closer together — vertically — than those
 * floors, e.g. an 18pt heading directly over a 13pt body at tight leading.
 * When that happens, one block's cover rect (used for masking + save-time
 * whiteout) can bleed into its neighbor's actual ink.
 *
 * For every pair of blocks whose TIGHT bounds overlap horizontally (x-ranges
 * intersect) and whose COVER rects overlap vertically, this clamps the
 * facing cover edges — but never past each block's own ink-safety bound
 * (CLAMP_SAFETY_ABOVE/BELOW em, see above) even when that crosses the raw
 * midpoint: bleeding into the inter-block WHITESPACE is harmless, exposing
 * ink is not. A proportional split (weighted by each side's own safety
 * requirement — see below) is used ONLY as a last resort, when the two
 * blocks' safety-padded bounds themselves still overlap (their real inks are
 * close enough that no boundary line can fully protect both sides — a
 * genuine bounding-box limitation with no glyph-outline data available at
 * detection time; this is a measured-smaller-but-nonzero residual, not a
 * full fix).
 *
 * O(n log n) via a top-sort + neighbor-window scan: pages have tens of
 * blocks, not thousands, so a full sort plus a short forward scan per block
 * (stopping once a candidate's tight top is below the current block's tight
 * bottom, i.e. no more vertical proximity is possible) is more than enough —
 * no need for an interval tree.
 */
function clampAdjacentCoverBleed(blocks) {
  if (blocks.length < 2) return;

  const sorted = [...blocks].sort((a, b) => a._tightTop - b._tightTop);

  for (let i = 0; i < sorted.length; i++) {
    const upper = sorted[i];
    for (let j = i + 1; j < sorted.length; j++) {
      const lower = sorted[j];

      // Once a later block's tight top starts well below the upper block's
      // tight bottom, no cover-rect overlap is possible (cover rects can
      // only extend the floors above/below the tight box) — stop scanning
      // forward for this `upper`. A little slack (the larger of the two
      // blocks' own heights) keeps this a safe early-out, not a tight one.
      const slack = Math.max(upper._tightBottom - upper._tightTop, lower._tightBottom - lower._tightTop, 1);
      if (lower._tightTop > upper._tightBottom + slack) break;

      // Only ever clamp a block directly against the nearest one above it
      // (upper._tightTop <= lower._tightTop, already guaranteed by the sort).
      if (lower._tightTop < upper._tightTop) continue;

      // Horizontal (tight-bound) overlap check. originalBounds.x/.width are
      // the tight (un-padded) text extents (see its doc comment above) —
      // block.x/.width are the padded display box and would over-count
      // overlap for blocks that are tight-adjacent but padding-touching.
      const upperX0 = upper.originalBounds.x;
      const upperX1 = upper.originalBounds.x + upper.originalBounds.width;
      const lowerX0 = lower.originalBounds.x;
      const lowerX1 = lower.originalBounds.x + lower.originalBounds.width;
      const xOverlaps = upperX0 < lowerX1 && lowerX0 < upperX1;
      if (!xOverlaps) continue;

      // Vertical cover-rect overlap check.
      const upperCoverBottom = upper.originalBounds.y + upper.originalBounds.height;
      const lowerCoverTop = lower.originalBounds.y;
      if (lowerCoverTop >= upperCoverBottom) continue; // no bleed — floors already fit

      // Ink-safety bounds: how far EACH block's own cover edge is allowed to
      // retreat toward its neighbor before risking that block's own ink.
      // upperSafeBottom = the lowest the upper block's coverBottom may sit
      // without risking the upper block's OWN descender ink; lowerSafeTop =
      // the highest the lower block's coverTop may sit without risking the
      // lower block's OWN ascender ink.
      const upperSafeBottom = upper._tightBottom + CLAMP_SAFETY_BELOW * upper.fontSize;
      const lowerSafeTop = lower._tightTop - CLAMP_SAFETY_ABOVE * lower.fontSize;

      if (upperSafeBottom <= lowerSafeTop) {
        // Safety-padded bounds don't overlap — clamp each block to its own
        // safe edge. Any resulting gap between them is blank inter-block
        // whitespace, never ink.
        if (upperCoverBottom > upperSafeBottom) {
          const newHeight = upperSafeBottom - upper.originalBounds.y;
          upper.originalBounds = { ...upper.originalBounds, height: Math.max(0, newHeight) };
        }
        if (lowerCoverTop < lowerSafeTop) {
          const newBottom = lower.originalBounds.y + lower.originalBounds.height;
          lower.originalBounds = { ...lower.originalBounds, y: lowerSafeTop, height: Math.max(0, newBottom - lowerSafeTop) };
        }
      } else {
        // Even safety-padded bounds overlap — the two blocks' TIGHT bounds
        // are close enough (or already overlapping) that no boundary line
        // can satisfy both blocks' full safety margins at once: giving the
        // lower block's ascent its full margin would whiteout part of the
        // upper block's own surviving descender ink, and vice versa. This is
        // a genuine bounding-box limitation (no glyph-outline data at
        // detection time) — measured in the browser harness, the residual
        // exposure in this branch is real but small relative to the
        // uncorrected pre-fix midpoint (which ignored safety margins
        // entirely).
        //
        // Weighted average of the two DESIRED safety positions (upperSafeBottom,
        // lowerSafeTop), weighted so the side with the LARGER requirement
        // (almost always the ascent/CLAMP_SAFETY_ABOVE side, since measured
        // ascent-metric error (~0.20-0.22em) dwarfs measured descent-metric
        // error (~0.01em)) pulls the split further toward ITS OWN desired
        // position — i.e. gets more of what it wants — rather than a naive
        // 50/50 split of the tight-bound gap (which left the ascent side
        // under-protected). Weight-by-OWN-want (not the other side's) is
        // intentional: it's a direction-safe formulation that behaves
        // correctly whether the tight bounds have a small positive gap or
        // are already inverted/overlapping (interpolating directly between
        // the tight bounds extrapolates outside them in the inverted case).
        // Finally clamped to lie within [min(tight), max(tight)] — the split
        // can favor one side heavily, but never past where either block's
        // OWN reported-metric tight ink is known to end.
        const upperWant = CLAMP_SAFETY_BELOW * upper.fontSize;
        const lowerWant = CLAMP_SAFETY_ABOVE * lower.fontSize;
        const totalWant = upperWant + lowerWant;
        // totalWant > 0 always (both constants are positive), so this is a
        // safe divide with no zero-total edge case to guard.
        let splitPoint = (lowerSafeTop * lowerWant + upperSafeBottom * upperWant) / totalWant;
        const tightLo = Math.min(upper._tightBottom, lower._tightTop);
        const tightHi = Math.max(upper._tightBottom, lower._tightTop);
        splitPoint = Math.max(tightLo, Math.min(tightHi, splitPoint));

        if (upperCoverBottom > splitPoint) {
          const newHeight = splitPoint - upper.originalBounds.y;
          upper.originalBounds = { ...upper.originalBounds, height: Math.max(0, newHeight) };
        }
        if (lowerCoverTop < splitPoint) {
          const newBottom = lower.originalBounds.y + lower.originalBounds.height;
          lower.originalBounds = { ...lower.originalBounds, y: splitPoint, height: Math.max(0, newBottom - splitPoint) };
        }
      }
    }
  }
}


/**
 * Detect embedded images on a page.
 * Uses PDF.js operator list to find paintImageXObject operations.
 */
async function detectImages(page, pageNum, _pageWidth, pageHeight) {
  // Our editable images already restore from the sidecar. Reading annotation
  // appearances too would discover those same images a second time.
  const ops = await page.getOperatorList({ annotationMode: pdfjsLib.AnnotationMode.DISABLE });
  const images = [];
  let imageId = 0;
  let matrix = [1, 0, 0, 1, 0, 0];
  let fillAlpha = 1;
  let rectangle = null;
  const stack = [];
  const multiply = (m, n) => [m[0]*n[0]+m[2]*n[1], m[1]*n[0]+m[3]*n[1], m[0]*n[2]+m[2]*n[3], m[1]*n[2]+m[3]*n[3], m[0]*n[4]+m[2]*n[5]+m[4], m[1]*n[4]+m[3]*n[5]+m[5]];

  for (let i = 0; i < ops.fnArray.length; i++) {
    const fn = ops.fnArray[i];
    const args = ops.argsArray[i];
    // PDF placement is the product of ALL active transforms. Looking back
    // for the last `cm` finds an identity skew matrix in pdf-lib output and
    // incorrectly treats a large photograph as a 1pt decorative image.
    if (fn === pdfjsLib.OPS.save || fn === pdfjsLib.OPS.paintFormXObjectBegin) {
      stack.push({ matrix: matrix.slice(), fillAlpha });
      if (fn === pdfjsLib.OPS.paintFormXObjectBegin && args[0]) matrix = multiply(matrix, args[0]);
    } else if (fn === pdfjsLib.OPS.restore || fn === pdfjsLib.OPS.paintFormXObjectEnd) {
      const state = stack.pop();
      matrix = state?.matrix || [1, 0, 0, 1, 0, 0]; fillAlpha = state?.fillAlpha ?? 1;
    } else if (fn === pdfjsLib.OPS.transform) matrix = multiply(matrix, args);
    else if (fn === pdfjsLib.OPS.setGState) {
      for (const [key, value] of args[0]) if (key === 'ca') fillAlpha = value;
    } else if (fn === pdfjsLib.OPS.constructPath) {
      rectangle = opaqueRectangle(args[0], args[1], matrix);
    } else if ([pdfjsLib.OPS.fill, pdfjsLib.OPS.eoFill, pdfjsLib.OPS.fillStroke, pdfjsLib.OPS.eoFillStroke, pdfjsLib.OPS.closeFillStroke, pdfjsLib.OPS.closeEOFillStroke].includes(fn)) {
      if (rectangle && fillAlpha === 1) {
        // The editor's legacy image replacement uses an opaque rectangle to
        // cover the source image. Do not offer an invisible, covered image
        // again on reopen (doing so would resurrect a deleted picture).
        for (let index = images.length - 1; index >= 0; index--) {
          const image = images[index];
          const bottom = pageHeight - image.y - image.height;
          if (image.x >= rectangle.x - .01 && bottom >= rectangle.y - .01 && image.x + image.width <= rectangle.x + rectangle.width + .01 && bottom + image.height <= rectangle.y + rectangle.height + .01) images.splice(index, 1);
        }
      }
      rectangle = null;
    } else if (fn === pdfjsLib.OPS.endPath || fn === pdfjsLib.OPS.stroke) rectangle = null;

    // OPS.paintImageXObject = 85
    if (fn === pdfjsLib.OPS.paintImageXObject || fn === pdfjsLib.OPS.paintJpegXObject || fn === pdfjsLib.OPS.paintInlineImageXObject) {
      const imgName = ops.argsArray[i][0];

      try {
        // Get the image object from the page's objs
        const imgData = fn === pdfjsLib.OPS.paintInlineImageXObject ? args[0] : await new Promise((resolve, reject) => {
          const objects = imgName.startsWith('g_') ? page.commonObjs : page.objs;
          objects.get(imgName, (data) => {
            if (data) resolve(data);
            else reject(new Error('No image data'));
          });
        });

        if (!imgData || !imgData.width || !imgData.height) continue;

        // Extract image as data URL via canvas
        const canvas = document.createElement('canvas');
        canvas.width = imgData.width;
        canvas.height = imgData.height;
        const ctx = canvas.getContext('2d');

        // imgData may be ImageData or have a bitmap
        if (imgData.bitmap) {
          ctx.drawImage(imgData.bitmap, 0, 0);
        } else if (imgData.data) {
          const count = imgData.width * imgData.height;
          const pixels = new Uint8ClampedArray(count * 4);
          if (imgData.data.length === pixels.length) pixels.set(imgData.data);
          else for (let pixel = 0; pixel < count; pixel++) {
            const rgba = pixel * 4;
            if (imgData.kind === pdfjsLib.ImageKind.RGB_24BPP) {
              pixels[rgba] = imgData.data[pixel * 3]; pixels[rgba + 1] = imgData.data[pixel * 3 + 1]; pixels[rgba + 2] = imgData.data[pixel * 3 + 2];
            } else {
              const row = Math.floor(pixel / imgData.width), col = pixel % imgData.width;
              const value = imgData.data[row * Math.ceil(imgData.width / 8) + (col >> 3)] & (128 >> (col % 8)) ? 255 : 0;
              pixels[rgba] = pixels[rgba + 1] = pixels[rgba + 2] = value;
            }
            pixels[rgba + 3] = 255;
          }
          const imageData = new ImageData(pixels, imgData.width, imgData.height);
          ctx.putImageData(imageData, 0, 0);
        } else {
          continue;
        }

        const [a, b, c, d, e, f] = matrix;
        const xs = [e, a + e, c + e, a + c + e], ys = [f, b + f, d + f, b + d + f];
        const imgX = Math.min(...xs), maxY = Math.max(...ys);
        const imgY = pageHeight - maxY, imgW = Math.max(...xs) - imgX, imgH = maxY - Math.min(...ys);

        // Skip tiny images (likely artifacts, bullets, or decorative elements)
        if (imgW < 20 || imgH < 20) continue;

        // Preserve any rotation/mirroring already applied by the PDF when
        // turning the image into an axis-aligned editable overlay.
        const oriented = document.createElement('canvas');
        const scale = Math.min(Math.max(imgData.width / imgW, imgData.height / imgH), 4096 / Math.max(imgW, imgH));
        oriented.width = Math.max(1, Math.round(imgW * scale)); oriented.height = Math.max(1, Math.round(imgH * scale));
        const output = oriented.getContext('2d');
        output.setTransform(a / imgData.width * scale, -b / imgData.width * scale, -c / imgData.height * scale, d / imgData.height * scale, (c + e - imgX) * scale, (maxY - d - f) * scale);
        output.drawImage(canvas, 0, 0);
        const dataUrl = oriented.toDataURL('image/png');

        images.push({
          id: `detected-img-${pageNum}-${imageId++}`,
          pageNum,
          x: imgX,
          y: imgY,
          width: imgW,
          height: imgH,
          dataUrl,
          originalBounds: { x: imgX, y: imgY, width: imgW, height: imgH },
        });
      } catch (_) {
        // Skip images that fail to extract
      }
    }
  }

  return images;
}

function opaqueRectangle(ops, coordinates, matrix) {
  const { rectangle, moveTo, lineTo, closePath } = pdfjsLib.OPS;
  let points;
  if (ops.length === 1 && ops[0] === rectangle) {
    const [x, y, w, h] = coordinates;
    points = [[x, y], [x + w, y], [x + w, y + h], [x, y + h]];
  } else if (ops.length === 5 && ops[0] === moveTo && ops.slice(1, 4).every((op) => op === lineTo) && ops[4] === closePath) {
    points = Array.from({ length: 4 }, (_, i) => [coordinates[i * 2], coordinates[i * 2 + 1]]);
  } else return null;
  const [a, b, c, d, e, f] = matrix;
  points = points.map(([x, y]) => [a * x + c * y + e, b * x + d * y + f]);
  // Only an axis-aligned rectangle covers its full bounds; a rotated or
  // arbitrary path must not hide an image just because its bounds overlap.
  if (points.some((p, i) => { const next = points[(i + 1) % 4]; return Math.abs(p[0] - next[0]) > .001 && Math.abs(p[1] - next[1]) > .001; })) return null;
  const xs = points.map((p) => p[0]), ys = points.map((p) => p[1]);
  return { x: Math.min(...xs), y: Math.min(...ys), width: Math.max(...xs) - Math.min(...xs), height: Math.max(...ys) - Math.min(...ys) };
}

// --- Helpers ---

/** Find the most common value in an array. */
function mode(arr) {
  const counts = {};
  let maxCount = 0, maxVal = arr[0];
  for (const v of arr) {
    counts[v] = (counts[v] || 0) + 1;
    if (counts[v] > maxCount) { maxCount = counts[v]; maxVal = v; }
  }
  return maxVal;
}

/** Map PDF.js font names to our supported font families. */
export function sanitizeFontFamily(pdfFontName) {
  if (!pdfFontName) return 'Helvetica';
  const lower = pdfFontName.toLowerCase();
  if (lower.includes('times')) return 'Times-Roman';
  if (lower.includes('courier') || lower.includes('mono')) return 'Courier';
  // Arial is its own selectable family so reopening an Arial PDF shows "Arial"
  // in the font picker instead of silently relabelling it Helvetica. The two
  // bake to the same Standard-14 font (see resolveFontKey), so the saved bytes
  // are unchanged by this distinction — only the label the user sees differs.
  if (lower.includes('arial')) return 'Arial';
  if (lower.includes('helvetica') || lower.includes('sans')) return 'Helvetica';
  if (lower.includes('roboto') && lower.includes('slab')) return 'Roboto Slab';
  // Synthetic PDF.js font names (e.g. g_d2_f6, g_d6_f4) — internal IDs with no real name
  if (/^g_d\d+_f\d+$/i.test(pdfFontName)) return 'Helvetica';
  // Other unrecognizable names (contain only underscores/digits, or very short) — default
  if (/^[a-z]_/.test(lower) || pdfFontName.length <= 3) return 'Helvetica';
  // Return as-is for recognizable font names — CSS will fall back
  return pdfFontName;
}

/** Detect text alignment from line start positions relative to block width. */
function detectAlignment(lineStartXs, blockWidth) {
  if (lineStartXs.length <= 1) return 'left';
  const avgOffset = lineStartXs.reduce((a, b) => a + b, 0) / lineStartXs.length;
  if (avgOffset > blockWidth * 0.35) return 'center';
  if (lineStartXs.every((x) => x > blockWidth * 0.5)) return 'right';
  return 'left';
}
